// Realtime-Selbstheilung: Nach Standby läuft das Access-Token ab und der
// Realtime-Server lehnt den Rejoin ab (InvalidJWTToken "exp"). supabase-js
// erneuert das Token erst mit Verzögerung — bis dahin wäre der Socket tot.
// Pure Funktionen ohne DOM-Abhängigkeit (kein jsdom im Projekt): Client,
// Channel-Fabrik und Timer werden injiziert.

interface SupabaseLike {
  auth: { getSession: () => Promise<{ data: { session: { access_token: string } | null } }> };
  realtime?: { setAuth?: (token: string) => void | Promise<unknown> };
  removeChannel: (channel: any) => void;
}

// setAuth ist ab supabase-js ~2.4x async und rejected mit einem String
// ("InvalidJWTToken ... exp"), wenn das Token beim Absenden schon abgelaufen
// ist. Unbeobachtet landet das als unhandled rejection im Bug-Tracker; die
// Recovery/der nächste Refresh heilen den Zustand ohnehin.
const applyRealtimeAuth = (client: SupabaseLike, token: string): void => {
  try {
    void Promise.resolve(client.realtime?.setAuth?.(token)).catch(() => {});
  } catch { /* sync-Throw alter Versionen — gleiches Kalkül */ }
};

interface SubscribableChannel {
  subscribe: (cb: (status: string) => void) => any;
}

export interface RecoveryOptions {
  maxRetries?: number;
  retryDelayMs?: number;
  schedule?: (fn: () => void, ms: number) => any;
  cancel?: (handle: any) => void;
  /**
   * Der Kanal steht nach einem Abriss wieder (SUBSCRIBED nach CHANNEL_ERROR,
   * TIMED_OUT oder CLOSED) — ob durch die Recovery hier oder durch das
   * Rejoin von realtime-js. Was in der Luecke geschah, kam nie an; wer
   * darauf angewiesen ist, holt hier nach. Das erste SUBSCRIBED zaehlt nicht.
   */
  onResubscribed?: () => void;
  /**
   * Der Kanal steht — bei JEDEM SUBSCRIBED, auch dem ersten. Fuer Abonnenten,
   * die die Luecke zwischen ihrem letzten Abruf und dem Beitritt selbst
   * schliessen (Projektliste im Signalmodus, App.tsx). Wird NACH
   * `onResubscribed` gerufen.
   */
  onSubscribed?: () => void;
}

export interface ResilientHandle {
  close: () => void;
}

const RETRYABLE = new Set(['CHANNEL_ERROR', 'TIMED_OUT']);

export const subscribeWithAuthRecovery = (
  client: SupabaseLike,
  buildChannel: () => SubscribableChannel,
  opts: RecoveryOptions = {}
): ResilientHandle => {
  const {
    maxRetries = 5,
    retryDelayMs = 2000,
    schedule = (fn, ms) => setTimeout(fn, ms),
    cancel = (h) => clearTimeout(h),
    onResubscribed,
    onSubscribed,
  } = opts;

  let closed = false;
  let retries = 0;
  let current: SubscribableChannel | null = null;
  let pending = false; // genau eine Recovery zugleich; Handle 0 wäre falsy, daher eigenes Flag
  let timer: any = null;
  // Generationszähler: SUBSCRIBED/close entwerten damit auch eine Recovery,
  // die schon läuft (im getSession hängt) und nicht mehr cancelbar ist.
  let recoverySeq = 0;
  // Ueber alle neu aufgebauten Channels hinweg: war der Kanal schon einmal
  // verbunden, und ist er seitdem abgerissen?
  let warVerbunden = false;
  let abgerissen = false;

  const clearPending = () => {
    recoverySeq += 1;
    if (pending && timer !== null) cancel(timer);
    pending = false;
    timer = null;
  };

  const start = () => {
    if (closed) return;
    const channel = buildChannel();
    current = channel;
    channel.subscribe((status) => {
      // Nur der aktive Channel darf Recovery auslösen — Status-Nachzügler
      // eines bereits ersetzten Channels würden sonst doppelt neu aufbauen.
      if (closed || channel !== current) return;
      if (status === 'SUBSCRIBED') {
        retries = 0;
        // Channel hat sich selbst erholt — geplanter Abriss wäre kontraproduktiv.
        clearPending();
        const wieder = warVerbunden && abgerissen;
        warVerbunden = true;
        abgerissen = false;
        if (wieder && onResubscribed) {
          try { onResubscribed(); } catch { /* Nachholen darf den Kanal nie stoeren */ }
        }
        if (onSubscribed) {
          try { onSubscribed(); } catch { /* ebenso */ }
        }
        return;
      }
      if (warVerbunden && (RETRYABLE.has(status) || status === 'CLOSED')) abgerissen = true;
      if (RETRYABLE.has(status) && !pending && retries < maxRetries) {
        retries += 1;
        pending = true;
        const myGen = recoverySeq;
        const stillValid = () => !closed && myGen === recoverySeq && channel === current;
        const handle = schedule(async () => {
          timer = null;
          if (!stillValid()) { pending = false; return; }
          try {
            // getSession refresht ein abgelaufenes Token automatisch.
            const { data: { session } } = await client.auth.getSession();
            if (session?.access_token) applyRealtimeAuth(client, session.access_token);
          } catch {
            // Offline o.ä. — der nächste CHANNEL_ERROR versucht es erneut.
          }
          // Lock bleibt bis hier gehalten: kein zweiter Lauf, solange der
          // Refresh in-flight ist. Erst nach allen Checks freigeben.
          if (!stillValid()) { pending = false; return; }
          try { client.removeChannel(channel); } catch { /* schon entfernt */ }
          pending = false;
          start();
        }, retryDelayMs * retries);
        // Synchrone Scheduler (Tests) haben den Task hier schon ausgeführt —
        // dann kein Handle merken, sonst würde ein erledigter Timer gecancelt.
        if (pending) timer = handle;
      }
    });
  };

  start();

  return {
    close: () => {
      closed = true;
      clearPending();
      if (current) {
        try { client.removeChannel(current); } catch { /* schon entfernt */ }
      }
    },
  };
};

interface DocumentLike {
  visibilityState?: string;
  addEventListener: (ev: string, fn: () => void) => void;
  removeEventListener: (ev: string, fn: () => void) => void;
}

/**
 * Schiebt Realtime nach dem Aufwachen des Tabs proaktiv ein frisches Token
 * zu, bevor der Socket mit dem abgelaufenen Token rejoint.
 * Rückgabe: Detach-Funktion für useEffect-Cleanup.
 */
export const attachWakeAuthRefresh = (client: SupabaseLike, doc: DocumentLike): (() => void) => {
  const onVisibility = () => {
    if (doc.visibilityState !== 'visible') return;
    void (async () => {
      try {
        const { data: { session } } = await client.auth.getSession();
        if (session?.access_token) applyRealtimeAuth(client, session.access_token);
      } catch {
        // Kein Netz beim Aufwachen — Channel-Recovery fängt es später ab.
      }
    })();
  };
  doc.addEventListener('visibilitychange', onVisibility);
  return () => doc.removeEventListener('visibilitychange', onVisibility);
};
